import React, { useEffect, useMemo, useState } from 'react';
import {
    TextField,
    Checkbox,
    FormControlLabel,
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import Crumbs from 'components/crumbs/Crumbs';
import CustomCard from 'components/common/CustomCard';
import CardHeader from 'components/common/CardHeader';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Container from 'components/common/Container';
import { createPermission, fetchPermission, updatePermission } from './data-apis';
import { displayNotification, generateOptionsFromData } from 'helpers';
import Button from 'components/common/Button';
import { getList } from '../meta-data/data-api';

const crumbs = [
    {
        path: "/",
        label: "Home",
        active: false,
    },
    {
        path: "/portal/permissions",
        label: "Permissions Management",
        active: false,
    },
    {
        path: "/portal/permissions/create",
        label: "Create",
        active: true,
    },
];


const PermissionForm = () => {

    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        resource: '',
        action: '',
        is_active: true,
        type: '',
        permission_group: '',
    });
    const [permissionGroups, setPermissionGroups] = useState([]);

    const [loaders, setLoaders] = useState({
        fetching: false,
        creating: false,
        updating: false,
    });

    useEffect(() => {
        if (id) {
            getPermission();
        }
        getPermissionGroups();
    }, [id]);

    const getPermission = async () => {
        setLoaders({ ...loaders, fetching: true });
        const res = await fetchPermission(id);
        if (res.success && res.status === 200) {
            setFormData({
                name: res.data.name,
                description: res.data.description,
                resource: res.data.resource,
                action: res.data.action,
                is_active: res.data.is_active,
                type: res.data.type,
                permission_group: res.data.permission_group,
            });
        }
        setLoaders({ ...loaders, fetching: false });
    }

    const getPermissionGroups = async () => {
        try {
            const groups = await getList('PRG');
            setPermissionGroups(groups?.relationListMaster || []);
        } catch (e) {
            console.log(e);
        }
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoaders({ ...loaders, creating: true });
        if (id) {
            const res = await updatePermission(id, formData);
            if (res.success && res.status === 200) {
                navigate('/portal/permissions');
                displayNotification('success', 'Permission updated successfully');
            }
        } else {
            const res = await createPermission(formData);
            if (res.success && res.status === 201) {
                // navigate('/portal/permissions');
                displayNotification('success', 'Permission created successfully');
                setFormData({
                    name: '',
                    resource: '',
                    action: '',
                    is_active: true,
                    type: '',
                    description: '',
                });
            } else {
                displayNotification('error', 'Permission creation failed');
            }
        }
        setLoaders({ ...loaders, creating: false });
    };

    const groupsOptions = useMemo(() => {
        return generateOptionsFromData(permissionGroups, { value: 'id', label: 'list_element_name' });
    }, [permissionGroups])

    return (
        <Container className="my-2">
            <Crumbs crumbs={crumbs} />
            <CustomCard>
                <CardHeader>
                    <Grid container justifyContent="space-between">
                        <Grid item><h2 className="text-2xl font-semibold mb-4">{id ? 'Update' : 'Create'} Permission</h2></Grid>
                        <Grid item>
                            <Button onClick={() => navigate('/portal/permissions')}>
                                <i className="fa-solid fa-arrow-left"></i> Go Back
                            </Button>
                        </Grid>
                    </Grid>
                </CardHeader>
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Permission Form
                        </Typography>
                        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                label="Name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                margin="normal"
                            />
                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                margin="normal"
                            />
                            <TextField
                                required
                                fullWidth
                                label="Resource"
                                name="resource"
                                value={formData.resource}
                                onChange={handleChange}
                                margin="normal"
                            />
                            <TextField
                                required
                                fullWidth
                                label="Action"
                                name="action"
                                value={formData.action}
                                onChange={handleChange}
                                margin="normal"
                            />
                            <small className="text-muted">Type (1) is static permissions. Type (2) is dynamic permissions.</small>
                            <TextField
                                required
                                fullWidth
                                type='number'
                                label="Type"
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                                margin="normal"
                            />
                            <FormControl fullWidth>
                                <InputLabel id="group-select-label">Select Group</InputLabel>
                                <Select
                                    labelId="permission_group"
                                    name="permission_group"
                                    value={formData.permission_group}
                                    label="Select Group"
                                    onChange={handleChange}
                                >
                                    <MenuItem key={''} value={''}>
                                        None
                                    </MenuItem>
                                    {groupsOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <small>
                                    <Link to='/portal/meta-data/list-elements/PRG'>
                                        *Add New Group
                                    </Link>
                                </small>
                            </FormControl>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.is_active}
                                        onChange={handleChange}
                                        name="is_active"
                                    />
                                }
                                label="Is Active"
                                sx={{ mt: 2 }}
                            />
                            <Box sx={{ mt: 3 }}>
                                <Button
                                    type="submit"
                                    variant="secondary"
                                    isLoading={loaders.creating}
                                    disabled={loaders.creating}
                                >
                                    {loaders.creating ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-solid fa-save"></i>}
                                    Save Permission
                                </Button>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </CustomCard>
        </Container>
    );
};

export default PermissionForm;
